import { Box, InputBase, useMediaQuery } from "@mui/material";

interface OTPInputProps {
  length?: number;
  value?: string;
  onChange: (val: string) => void;
  onComplete?: (val: string) => void;
  [key: string]: any;
}

export default function OTPInput({
  length = 6,
  value = "",
  onChange,
  onComplete,
  ...props
}: OTPInputProps) {
  const values = Array.from({ length }, (_, i) => value[i] || "");
  const isSmall = useMediaQuery("(max-width:400px)");

  const focusInput = (idx: number) => {
    setTimeout(() => {
      document.getElementById(`otp-input-${idx}`)?.focus();
    }, 0);
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>, idx: number) => {
    const char = e.target.value.replace(/\D/g, "");
    if (!char) return;
    const next = value.slice(0, idx) + char[0] + value.slice(idx + 1, length);
    onChange(next.slice(0, length));

    if (char && idx < length - 1) {
      focusInput(idx + 1);
    }

    if (next.length === length && /^[0-9]+$/.test(next)) {
      setTimeout(() => {
        onComplete?.(next);
      }, 50);
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    idx: number
  ) => {
    if (e.key === "Backspace") {
      if (values[idx]) {
        const prev = value.slice(0, idx) + "" + value.slice(idx + 1, length);
        onChange(prev);
      } else if (idx > 0) {
        focusInput(idx - 1);
      }
    }
    if (e.key === "ArrowLeft" && idx > 0) {
      focusInput(idx - 1);
    }
    if (e.key === "ArrowRight" && idx < length - 1) {
      focusInput(idx + 1);
    }
    if (e.key === "Enter") {
      const currentValue = values.join("");
      if (currentValue.length === length && /^[0-9]+$/.test(currentValue)) {
        onComplete?.(currentValue);
      }
      (e.target as HTMLInputElement).form?.dispatchEvent(
        new Event("submit", { cancelable: true, bubbles: true })
      );
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const paste = e.clipboardData.getData("text").replace(/\D/g, "");
    if (paste) {
      const val = paste.slice(0, length);
      onChange(val);
      setTimeout(() => {
        focusInput(val.length - 1);
        if (val.length === length) {
          onComplete?.(val);
        }
      }, 10);
    }
    e.preventDefault();
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        flexWrap: "wrap",
        gap: 1,
        mb: 2,
      }}
    >
      {values.map((v, i) => (
        <InputBase
          key={i}
          id={`otp-input-${i}`}
          value={v}
          onChange={(e) => handleInput(e as any, i)}
          onKeyDown={(e) => handleKeyDown(e as any, i)}
          onPaste={i === 0 ? handlePaste : undefined}
          inputProps={{
            style: {
              width: isSmall ? 32 : 38,
              height: isSmall ? 42 : 48,
              textAlign: "center",
              fontSize: 22,
              border: "2px solid #d1d7e6",
              borderRadius: 10,
              background: "#f6f8fb",
              outline: "none",
              transition: "border-color 0.2s",
            },
            maxLength: 1,
            inputMode: "numeric",
            pattern: "[0-9]*",
            autoComplete: "one-time-code",
          }}
          {...(i === 0 ? { autoFocus: true } : {})}
          sx={{
            "& input:focus": {
              borderColor: "#1976d2",
            },
          }}
          {...props}
        />
      ))}
    </Box>
  );
}
